/**
 * CreateOrganizationModal.tsx — «Создать рабочее пространство» (WEB-281.4 P2).
 *
 * WEB-3796: вид — по эталону рескина (`WsForm`, `design/cabinet-rescin/app.jsx`):
 * `Modal` кита шириной 560 — та же ширина, что у окна «Новый проект»
 * (CreateProjectWizard.tsx, WEB-2232; узкие 440 были по старому макету, обратно
 * не возвращать), поле «Название» (`label.lbl`), `Notice` кита «Вы станете
 * владельцем…», подвал [Отмена, Создать]; итог — всплывашкой кита (`useToast`),
 * её узел — рядом с окном, а не внутри: закрытый `<dialog>` не рисует ничего.
 * Поведение прежнее: «Создать» погашена на пустом имени с причиной в `title`
 * (WEB-618 — без тихого закрытия, с сигналом почему); Enter в поле — отправка;
 * пустое имя по Enter — ошибка под полем; длина — прежние 64 знака полем (сервер
 * разрешает 255, ошибка «длиннее 255» полем недостижима). На submit —
 * createOrganizationThunk → добавляет+выбирает новую оргу, закрывает на успехе.
 */
import React, { useEffect, useState } from 'react';
import { localizeError } from '@/utils/localizeError';
import { Button, Modal, Notice, useToast } from '@/components/kit/ds';
import { useAppDispatch } from '@/hooks/hooks';
import {
  createOrganizationThunk,
  switchActiveOrgThunk,
  fetchOrganizationsThunk,
} from '@/store/organizationsSlice';
import {
  clearActiveProjectThunk,
  fetchProjectsThunk,
} from '@/store/projectsSlice';
import { fetchBillingThunk } from '@/store/billingSlice';
import { useStrings } from '@/i18n';
import { commonStrings } from '@/i18n/dictionaries/common';
import { settingsStrings } from '@/i18n/dictionaries/settings';

interface CreateOrganizationModalProps {
  open: boolean;
  onClose: () => void;
}

export function CreateOrganizationModal({ open, onClose }: CreateOrganizationModalProps) {
  const s = useStrings(settingsStrings);
  const c = useStrings(commonStrings);
  const dispatch = useAppDispatch();
  const [name, setName] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [nameError, setNameError] = useState('');
  const [toast, showToast] = useToast();

  // Сброс при закрытии (как в макете).
  useEffect(() => {
    if (!open) {
      setName('');
      setNameError('');
      setIsSubmitting(false);
    }
  }, [open]);

  const canSubmit = name.trim().length > 0;

  const handleSubmit = async () => {
    if (!canSubmit) {
      setNameError(s.orgCreateNameRequired);
      return;
    }
    if (isSubmitting) return;
    setIsSubmitting(true);
    try {
      const result = await dispatch(
        createOrganizationThunk({ name: name.trim() }),
      );
      if (createOrganizationThunk.fulfilled.match(result)) {
        // WEB-1028: создание делало новую организацию активной только ЛОКАЛЬНО,
        // и кабинет оставался прежним — разделы продолжали показывать данные
        // старой организации, потому что сервер по-прежнему считал активной её.
        // Выполняем тот же ритуал, что и обычное переключение (OrgTeam):
        // сказать серверу, сбросить активный проект чужой организации,
        // перезапросить проекты и баланс.
        const newOrgId = result.payload.id;
        const switchResult = await dispatch(switchActiveOrgThunk(newOrgId));
        // WEB-1040: отказ сервера раньше игнорировался — клиент продолжал
        // ритуал (сброс проекта, перезапрос данных «новой» организации) и
        // получалось полусостояние наоборот: локально активна новая, на
        // сервере старая. Теперь: честная ошибка, ритуал не выполняется,
        // список организаций перезапрашивается, чтобы локальный выбор
        // пересинхронизировался с серверной правдой.
        if (!switchActiveOrgThunk.fulfilled.match(switchResult)) {
          dispatch(fetchOrganizationsThunk());
          showToast(s.orgCreatedSwitchFailed(result.payload.name), 'err');
          onClose();
          return;
        }
        dispatch(clearActiveProjectThunk());
        dispatch(fetchProjectsThunk(newOrgId));
        dispatch(fetchBillingThunk(undefined));
        showToast(s.orgCreated(result.payload.name), 'ok');
        onClose();
      } else {
        // Ревизия пачки 29: после WEB-1955 отказ тханка — разобранный ApiErrorInfo,
        // не строка; показ идёт через localizeError (код → фраза по языку кабинета).
        showToast(localizeError(result.payload, s.orgCreateFailed), 'err');
      }
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleCancel = () => onClose();

  return (
    <>
      {toast}
      <Modal
        testid="create-org-modal"
        title={s.orgCreateTitle}
        open={open}
        onClose={handleCancel}
        width={560}
        footer={
          <>
            <Button kind="ghost" onClick={handleCancel} disabled={isSubmitting}>
              {c.cancel}
            </Button>
            <Button
              kind="primary"
              data-testid="create-org-submit-btn"
              loading={isSubmitting}
              disabled={!canSubmit}
              title={canSubmit ? undefined : s.orgCreateNameRequired}
              onClick={() => void handleSubmit()}
            >
              {s.create}
            </Button>
          </>
        }
      >
        <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
          <label className="lbl">
            <span>{s.orgCreateNameLabel}</span>
            <input
              className={'input' + (nameError ? ' bad' : '')}
              autoComplete="off"
              data-testid="create-org-name-input"
              placeholder={s.orgCreateNamePlaceholder}
              maxLength={64}
              value={name}
              onChange={(e) => {
                setName(e.target.value);
                setNameError('');
              }}
              onKeyDown={(e) => {
                if (e.key === 'Enter') void handleSubmit();
              }}
              autoFocus
            />
            {nameError && (
              <span className="hint bad" data-testid="create-org-name-error">
                {nameError}
              </span>
            )}
          </label>
          <Notice kind="info" title={s.orgCreateNoticeTitle}>
            {s.orgCreateNoticeText}
          </Notice>
        </div>
      </Modal>
    </>
  );
}
